// Rule-based "AI" insight text and recommendation ranking.
// Ported from the prototype; HTML strings became React nodes / structured data.

import React from 'react';
import { Activity, Moon, Repeat, Truck, Zap } from 'lucide-react';
import { toneVar } from '../ui';
import type { Tone } from '../ui';
import { GROUPS, REGIONS, avgFED, isfi, pct as pctDiff, readiness, workersInGroup, workersInRegion } from './data';
import type { Worker } from './data';

export type InsightScreen = 'overview' | 'past' | 'today' | 'forward' | 'shifts' | 'driving' | 'compliance';

export interface DriverOpts {
  scopeName?: string;
  isCompany?: boolean;
  regionId?: string;
}

export type InsightOpts = DriverOpts;

const isNightShift = (w: Worker): boolean => w.shifts.some((s) => s.in && (s.in >= '22:00' || s.in <= '06:00'));

/** Mean of the last-7-nights sleep, falling back to last night. */
const meanSleep = (w: Worker): number => (w.slp7 ? w.slp7.reduce((x, v) => x + v, 0) / w.slp7.length : w.slp);

/** Narrative for a screen; null when there is nothing to say. */
export function genInsight(screen: string, workers: Worker[], opts: InsightOpts = {}): React.ReactNode {
  if (!workers?.length) return null;
  const avgF = avgFED(workers);
  const critCount = workers.filter((w) => w.fed >= 80).length;
  const elevCount = workers.filter((w) => w.fed >= 60 && w.fed < 80).length;
  const notReadyCount = workers.filter((w) => readiness(w) === 2).length;
  const wtrCount = workers.filter((w) => w.wtrFlag).length;
  const n = workers.length;
  const pct = (v: number) => Math.round((v / n) * 100);
  const avgSlp = +(workers.reduce((a, w) => a + meanSleep(w), 0) / n).toFixed(1);
  const nightCount = workers.filter(isNightShift).length;
  const highConsec = workers.filter((w) => w.consec >= 5).length;
  const driveRisk = workers.filter((w) => w.driveKm >= 700).length;
  const over48 = workers.filter((w) => w.shiftHrs > 48).length;
  const scopeName = opts.scopeName || 'this group';

  // Highest-FED region for company scope
  let worstRegion: string | null = null;
  let worstRegFED = 0;
  if (opts.isCompany) {
    Object.entries(REGIONS).forEach(([rid, reg]) => {
      const rf = avgFED(workersInRegion(rid));
      if (rf > worstRegFED) {
        worstRegFED = rf;
        worstRegion = reg.name;
      }
    });
  }

  // Highest-FED group for region scope
  let worstGroup: string | null = null;
  let worstGroupFED = 0;
  if (opts.regionId) {
    REGIONS[opts.regionId].groups.forEach((gid) => {
      const gf = avgFED(workersInGroup(gid));
      if (gf > worstGroupFED) {
        worstGroupFED = gf;
        worstGroup = GROUPS[gid].name;
      }
    });
  }

  const b = (t: React.ReactNode, k: string) => <strong key={k}>{t}</strong>;
  const join = (parts: React.ReactNode[]): React.ReactNode =>
    parts.map((p, i) => (
      <React.Fragment key={i}>
        {i > 0 && ' '}
        {p}
      </React.Fragment>
    ));

  const sentences: Record<string, () => React.ReactNode> = {
    overview() {
      const parts: React.ReactNode[] = [];
      if (critCount)
        parts.push(
          <>
            {b(`${critCount} worker${critCount > 1 ? 's' : ''} (${pct(critCount)}%) are at critical FED ≥80`, 'a')}
            {' — immediate management review recommended.'}
          </>,
        );
      else if (elevCount)
        parts.push(
          <>
            {b(`${elevCount} worker${elevCount > 1 ? 's' : ''} are in the elevated FED zone (60–79)`, 'a')}
            {' — monitor closely and consider rota adjustments.'}
          </>,
        );
      else parts.push(`Fatigue levels across ${scopeName} are within manageable range (avg FED ${avgF}).`);
      if (notReadyCount)
        parts.push(
          `${notReadyCount} worker${notReadyCount > 1 ? 's are' : ' is'} flagged Not Ready for shift today based on biometric signals.`,
        );
      if (worstRegion) parts.push(`${worstRegion} is the highest-risk region at avg FED ${worstRegFED}.`);
      if (worstGroup)
        parts.push(
          <>
            {'Within this region, '}
            {b(worstGroup, 'g')}
            {` carries the highest fatigue load (avg FED ${worstGroupFED}).`}
          </>,
        );
      return join(parts);
    },
    past() {
      const parts: React.ReactNode[] = [];
      if (avgSlp < 6)
        parts.push(
          <>
            {'Average sleep of '}
            {b(`${avgSlp}h is below the 7h target`, 'a')}
            {` — out-of-work fatigue is a dominant driver for ${scopeName}.`}
          </>,
        );
      if (wtrCount)
        parts.push(
          <>
            {b(`${wtrCount} WTR breach${wtrCount > 1 ? 'es' : ''}`, 'a')}
            {' recorded this period; inadequate rest between shifts compounds cumulative fatigue.'}
          </>,
        );
      if (critCount)
        parts.push(`${critCount} worker${critCount > 1 ? 's have' : ' has'} crossed the critical FED threshold.`);
      else if (avgF > 55)
        parts.push(
          `The avg FED of ${avgF} indicates moderate-to-elevated fatigue accumulation — trending upward warrants attention.`,
        );
      else
        parts.push(
          `Fatigue trend for ${scopeName} is within acceptable bounds (avg FED ${avgF}) over the selected period.`,
        );
      return join(parts);
    },
    today() {
      const parts: React.ReactNode[] = [];
      if (notReadyCount)
        parts.push(
          <>
            {b(`${notReadyCount} of ${n} workers (${pct(notReadyCount)}%) are Not Ready`, 'a')}
            {" based on today's biometrics — supervisor follow-up required before shift start."}
          </>,
        );
      else parts.push(`All assessed workers in ${scopeName} have cleared pre-shift readiness checks today.`);
      if (wtrCount)
        parts.push(
          `${wtrCount} worker${wtrCount > 1 ? 's have' : ' has'} a WTR rest breach — fatigue risk is elevated even where readiness flags are not yet triggered.`,
        );
      if (avgSlp < 6.2)
        parts.push(
          `Group mean sleep last night was ${avgSlp}h — below the 6.5h alert threshold; in-shift vigilance may be degraded.`,
        );
      return join(parts);
    },
    forward() {
      const proj24 = workers.filter((w) => w.fed + (w.fed12[11] - w.fed12[8]) >= 80).length;
      const otElig = workers.filter((w) => w.restHrs >= 11 && w.consec < 6 && 100 - w.fed > 30).length;
      const parts: React.ReactNode[] = [];
      if (proj24)
        parts.push(
          <>
            {b(`${proj24} worker${proj24 > 1 ? 's are' : ' is'} projected to breach FED 80 within 24 hours`, 'a')}
            {' — rota intervention is recommended now to prevent duty-of-care risk.'}
          </>,
        );
      else parts.push(`No workers in ${scopeName} are projected to reach critical FED within 24 hours.`);
      if (otElig)
        parts.push(
          `${otElig} worker${otElig > 1 ? 's meet' : ' meets'} the eligibility criteria for overtime tonight — adequate rest and FED headroom confirmed.`,
        );
      if (highConsec)
        parts.push(
          `${highConsec} worker${highConsec > 1 ? 's have' : ' has'} worked ≥5 consecutive days — rest day scheduling should be prioritised in the next 7-day window.`,
        );
      return join(parts);
    },
    shifts() {
      const parts: React.ReactNode[] = [];
      if (nightCount)
        parts.push(
          <>
            {b(`${nightCount} worker${nightCount > 1 ? 's are' : ' is'} on night or early shifts`, 'a')}
            {' — circadian misalignment typically reduces sleep quality by 1–1.5h and elevates FED accumulation.'}
          </>,
        );
      if (avgSlp < 6.5)
        parts.push(
          `Mean sleep of ${avgSlp}h across ${scopeName} is below the 6.5h alert level — out-of-work fatigue is a significant contributor to current FED scores.`,
        );
      if (highConsec)
        parts.push(
          `${highConsec} worker${highConsec > 1 ? 's have' : ' has'} logged ≥5 consecutive days; cumulative load without adequate recovery increases injury risk.`,
        );
      if (!nightCount && avgSlp >= 6.5 && !highConsec)
        parts.push(
          `Shift patterns and sleep for ${scopeName} are within normal bounds — no circadian or recovery concerns flagged at this time.`,
        );
      return join(parts);
    },
    driving() {
      const driverW = workers.filter((w) => w.driveKm > 0);
      const parts: React.ReactNode[] = [];
      if (driveRisk)
        parts.push(
          <>
            {b(`${driveRisk} driver${driveRisk > 1 ? 's are' : ' is'} approaching EU weekly km limits`, 'a')}
            {' — tachograph review and schedule adjustment required to maintain compliance.'}
          </>,
        );
      if (driverW.length) {
        const dFED = avgFED(driverW);
        const ndFED = avgFED(workers.filter((w) => w.driveKm === 0));
        if (dFED - ndFED >= 8)
          parts.push(
            `Drivers carry an avg FED ${dFED - ndFED} points higher than non-drivers — driving workload is a material fatigue risk factor for this group.`,
          );
      }
      if (!driveRisk && driverW.length)
        parts.push(
          `All drivers in ${scopeName} are operating within EU weekly limits — no immediate compliance risk identified.`,
        );
      if (!driverW.length)
        parts.push(`No active drivers in ${scopeName} — driving fatigue risk does not apply to this group.`);
      return join(parts);
    },
    compliance() {
      const parts: React.ReactNode[] = [];
      if (wtrCount && over48)
        parts.push(
          <>
            {b(
              `${wtrCount} WTR breach${wtrCount > 1 ? 'es' : ''} and ${over48} worker${over48 > 1 ? 's' : ''} exceeding 48h/week`,
              'a',
            )}
            {` — dual compliance exposure in ${scopeName} requires immediate escalation.`}
          </>,
        );
      else if (wtrCount)
        parts.push(
          <>
            {b(`${wtrCount} WTR breach${wtrCount > 1 ? 'es require' : ' requires'} immediate action`, 'a')}
            {' — insufficient rest between shifts is both a legal obligation and a safety risk.'}
          </>,
        );
      else if (over48)
        parts.push(
          `${over48} worker${over48 > 1 ? 's are' : ' is'} working over 48h this week — within legal limits but elevated fatigue risk; monitor for WTR proximity.`,
        );
      else
        parts.push(
          `No WTR breaches or hours violations recorded for ${scopeName} in this period — compliance posture is good.`,
        );
      if (driveRisk)
        parts.push(
          `${driveRisk} driver${driveRisk > 1 ? 's are' : ' is'} at EU tachograph risk — log review and scheduling action needed.`,
        );
      return join(parts);
    },
  };
  const fn = sentences[screen];
  return fn ? fn() : null;
}

// ── Recommendations ──────────────────────────────────────────────────────────
export type DriverKey = 'sleep' | 'shifts' | 'output' | 'physio' | 'driving';

export interface Recommendation {
  key: DriverKey;
  /** 0–100 strength of the driver this recommendation belongs to */
  score: number;
  icon: React.ElementType;
  /** category label (Sleep, Scheduling, ...) */
  label: string;
  /** accent colour for the category */
  tone: Tone;
  /** CSS colour matching `tone`, for inline styles */
  color: string;
  action: string;
  detail: string;
}

/** Ranks fatigue drivers for the group and returns up to 4 specific next steps. */
export function analyseDrivers(workers: Worker[], _opts: InsightOpts = {}): Recommendation[] {
  if (!workers?.length) return [];
  const n = workers.length;
  const pct = (v: number) => Math.round((v / n) * 100);

  // SLEEP
  const avgSlp = +(workers.reduce((a, w) => a + meanSleep(w), 0) / n).toFixed(1);
  const sleepShort = workers.filter((w) => meanSleep(w) < 6).length;
  const sleepVsBase = workers.filter((w) => pctDiff(w.slp, w.slp_b) <= -8).length;
  const sleepScore = Math.min(
    100,
    (avgSlp < 6 ? 40 : avgSlp < 6.5 ? 25 : avgSlp < 7 ? 12 : 0) +
      (pct(sleepShort) > 40 ? 30 : pct(sleepShort) > 20 ? 18 : pct(sleepShort) > 10 ? 8 : 0) +
      (pct(sleepVsBase) > 40 ? 25 : pct(sleepVsBase) > 20 ? 15 : pct(sleepVsBase) > 10 ? 6 : 0),
  );

  // SHIFT SCHEDULING
  const nightW = workers.filter(isNightShift).length;
  const highConsec = workers.filter((w) => w.consec >= 5).length;
  const wtrBreachW = workers.filter((w) => w.wtrFlag).length;
  const over48 = workers.filter((w) => w.shiftHrs > 48).length;
  const shiftScore = Math.min(
    100,
    (pct(nightW) > 40 ? 35 : pct(nightW) > 20 ? 22 : pct(nightW) > 10 ? 10 : 0) +
      (pct(highConsec) > 30 ? 30 : pct(highConsec) > 15 ? 18 : pct(highConsec) > 5 ? 8 : 0) +
      (pct(wtrBreachW) > 20 ? 25 : pct(wtrBreachW) > 10 ? 15 : wtrBreachW > 0 ? 8 : 0) +
      (pct(over48) > 30 ? 10 : pct(over48) > 15 ? 6 : 0),
  );

  // OUTPUT (overall workload / FED)
  const avgF = avgFED(workers);
  const critW = workers.filter((w) => w.fed >= 80).length;
  const elevW = workers.filter((w) => w.fed >= 60 && w.fed < 80).length;
  const proj24 = workers.filter((w) => w.fed + (w.fed12[11] - w.fed12[8]) >= 80).length;
  const outputScore = Math.min(
    100,
    (avgF >= 80 ? 45 : avgF >= 65 ? 30 : avgF >= 55 ? 15 : avgF >= 45 ? 6 : 0) +
      (pct(critW) > 20 ? 35 : pct(critW) > 10 ? 22 : critW > 0 ? 10 : 0) +
      (pct(elevW) > 30 ? 15 : pct(elevW) > 15 ? 8 : 0) +
      (pct(proj24) > 20 ? 5 : 0),
  );

  // PHYSIOLOGICAL LOAD (HRV / RHR)
  const lowHRV = workers.filter((w) => pctDiff(w.hrv, w.hrv_b) <= -15).length;
  const highRHR = workers.filter((w) => pctDiff(w.rhr, w.rhr_b) >= 8).length;
  const notRdy = workers.filter((w) => readiness(w) === 2).length;
  const highISFI = workers.filter((w) => isfi(w) >= 75).length;
  const physioScore = Math.min(
    100,
    (pct(lowHRV) > 30 ? 35 : pct(lowHRV) > 15 ? 22 : pct(lowHRV) > 5 ? 10 : 0) +
      (pct(highRHR) > 30 ? 30 : pct(highRHR) > 15 ? 18 : pct(highRHR) > 5 ? 8 : 0) +
      (pct(notRdy) > 20 ? 25 : pct(notRdy) > 10 ? 15 : notRdy > 0 ? 7 : 0) +
      (pct(highISFI) > 20 ? 10 : 0),
  );

  // DRIVING
  const drivers = workers.filter((w) => w.driveKm > 0);
  const driveRisk = workers.filter((w) => w.driveKm >= 700).length;
  const driverFEDGap = drivers.length ? avgFED(drivers) - avgFED(workers.filter((w) => w.driveKm === 0)) : 0;
  const driveScore = !drivers.length
    ? 0
    : Math.min(
        100,
        (pct(driveRisk) > 25 ? 40 : pct(driveRisk) > 10 ? 24 : driveRisk > 0 ? 10 : 0) +
          (driverFEDGap >= 15 ? 35 : driverFEDGap >= 10 ? 22 : driverFEDGap >= 6 ? 10 : 0) +
          (pct(drivers.length) > 60 ? 20 : pct(drivers.length) > 30 ? 12 : 0),
      );

  type Driver = Omit<Recommendation, 'action' | 'detail' | 'color'>;
  const driversList: Driver[] = (
    [
      { key: 'sleep', score: sleepScore, icon: Moon, label: 'Sleep', tone: 'violet' },
      { key: 'shifts', score: shiftScore, icon: Repeat, label: 'Scheduling', tone: 'info' },
      { key: 'output', score: outputScore, icon: Zap, label: 'Workload', tone: 'bad' },
      { key: 'physio', score: physioScore, icon: Activity, label: 'Physiology', tone: 'good' },
      { key: 'driving', score: driveScore, icon: Truck, label: 'Driving', tone: 'warn' },
    ] as Driver[]
  )
    .filter((d) => d.score > 0)
    .sort((a, b) => b.score - a.score);

  if (driversList.length === 0) return [];

  const recs: Recommendation[] = [];
  const push = (driver: DriverKey, action: string, detail: string) => {
    const d = driversList.find((x) => x.key === driver);
    if (d) recs.push({ ...d, color: toneVar[d.tone], action, detail });
  };

  if (sleepScore > 0) {
    if (avgSlp < 6) {
      push(
        'sleep',
        'Review out-of-work recovery obligations',
        `Mean sleep of ${avgSlp}h is critically short. Consider mandatory minimum 8h off-duty windows and sleep hygiene guidance for affected workers.`,
      );
    } else if (sleepVsBase / n > 0.2) {
      push(
        'sleep',
        'Identify chronic sleep-debt workers for check-in',
        `${sleepVsBase} workers are sleeping ≥8% below their personal baseline — sustained deficit compounds FED faster than one-off short nights.`,
      );
    } else {
      push(
        'sleep',
        'Issue targeted sleep hygiene guidance',
        `Average sleep of ${avgSlp}h is below the 7h target. Briefing on sleep environment, screen time, and shift-transition routines may recover 0.5–1h per night.`,
      );
    }
  }

  if (shiftScore > 0) {
    if (wtrBreachW > 0) {
      push(
        'shifts',
        'Immediately remediate WTR breaches',
        `${wtrBreachW} worker${wtrBreachW > 1 ? 's have' : ' has'} less than 11h between shifts — a legal obligation. Adjust rota to restore minimum rest before next duty.`,
      );
    }
    if (highConsec > 0) {
      push(
        'shifts',
        'Schedule rest days for workers with ≥5 consecutive days',
        `${highConsec} worker${highConsec > 1 ? 's are' : ' is'} in the danger zone for cumulative load. A mandatory rest day within 48h will reduce FED trajectory by an estimated 8–12 points.`,
      );
    }
    if (nightW > 0) {
      push(
        'shifts',
        'Reduce back-to-back night rotations',
        `${nightW} night/early workers show elevated FED linked to circadian disruption. Limit consecutive nights to 3–4 and ensure forward-rotating shift patterns.`,
      );
    }
  }

  if (outputScore > 0) {
    if (critW > 0) {
      push(
        'output',
        'Lighten duties for critical-FED workers immediately',
        `${critW} worker${critW > 1 ? 's are' : ' is'} at FED ≥80 — peak fatigue risk. Reassign non-essential tasks, restrict solo or safety-critical work, and reassess after one full rest cycle.`,
      );
    } else if (avgF >= 65) {
      push(
        'output',
        'Review overall workload allocation',
        `Average FED of ${avgF} across the group indicates sustained overload. Consider redistributing tasks or scheduling lighter duties for the next 48–72h to allow FED to clear.`,
      );
    } else {
      push(
        'output',
        'Monitor output levels and proactively manage capacity',
        `FED is elevated (avg ${avgF}). Proactive rota management now prevents reaching critical thresholds; avoid adding overtime without checking individual headroom.`,
      );
    }
  }

  if (physioScore > 0) {
    if (notRdy > 0) {
      push(
        'physio',
        'Supervisor one-to-one before shift for Not-Ready workers',
        `${notRdy} worker${notRdy > 1 ? 's have' : ' has'} biometric flags (HRV/RHR/sleep deviation). A brief welfare check, lighter-duty assignment, or delayed start reduces acute physiological risk.`,
      );
    }
    if (lowHRV > 0) {
      push(
        'physio',
        'Investigate stress and recovery quality',
        `${lowHRV} worker${lowHRV > 1 ? 's show' : ' shows'} HRV suppression ≥15% below baseline — an indicator of unresolved physiological stress, not just sleep loss. Consider workload, mental load, and recovery quality.`,
      );
    }
  }

  if (driveScore > 0) {
    if (driveRisk > 0) {
      push(
        'driving',
        'Review tachograph logs and reschedule high-km drivers',
        `${driveRisk} driver${driveRisk > 1 ? 's are' : ' is'} approaching EU weekly km limits. Swap to depot duties or reduce route length for next 2–3 shifts to restore compliance headroom.`,
      );
    } else if (driverFEDGap >= 8) {
      push(
        'driving',
        'Rotate driving duties to distribute fatigue load',
        `Drivers carry ${driverFEDGap} FED points more than non-drivers on average. Periodic rotation to non-driving tasks within the week reduces the in-work fatigue contribution.`,
      );
    }
  }

  // Top 4, highest-scoring driver first, then fill from the remainder.
  const seen = new Set<string>();
  const ordered: Recommendation[] = [];
  for (const d of driversList) {
    const r = recs.find((x) => x.key === d.key && !seen.has(x.action));
    if (r) {
      seen.add(r.action);
      ordered.push(r);
    }
    if (ordered.length >= 4) break;
  }
  for (const r of recs) {
    if (!seen.has(r.action) && ordered.length < 4) {
      seen.add(r.action);
      ordered.push(r);
    }
  }
  return ordered;
}
